/**
 * React Query hook for fetching tax audit events
 *
 * Fetches detailed audit events for the audit view table
 * Supports filtering by tax year, error type, and resolution status
 */

import { useQuery } from "@tanstack/react-query";
import { authorizedAxios } from "@moonscape/shared";
import { useAuth0 } from "@auth0/auth0-react";

export interface AuditEvent {
  eventId: string;
  txnId: number;
  date: string;
  eventType: string;
  asset: string;
  quantity: number;
  exchange: string;
  exchangeDisplayName?: string;
  exchangeIconUrl?: string;
  errorType: string;
  errorMessage: string;
  errorCategory: string;
  priority: 'must_fix' | 'should_fix' | 'informational';
  resolutionStatus?: string;
  resolutionMethod?: 'confirmed-zero' | 'excluded' | 'data-fixed' | 'loss-writeoff' | 'self-transfer-confirmed';
  resolvedAt?: string;
  resolvedBy?: number;
  resolutionNotes?: string;
  createdAt: string;
  extTxnId?: string;
  tradeId?: string;
  error_context?: {
    suggested_resolution?: string;
    asset?: string;
    ledger?: any[];
    balance_before_txn?: number;
    missing_fields?: string[];
    possible_actions?: Array<{
      type: string;
      resolution_type: 'self-transfer-confirmed';
      label: string;
      description?: string;
    }>;
    potential_self_transfer_txns?: Array<{
      txn_id: number;
      ext_txn_id?: string | null;
      amount: number;
      timestamp: number;
      exchange?: string | null;
      type: string;
    }>;
    [key: string]: any;
  };
}

export interface AuditEventsResponse {
  success: boolean;
  data: {
    events: AuditEvent[];
    total: number;
  };
}

export interface UseAuditEventsParams {
  taxyear?: string;
  errorType?: string;
  status?: 'unresolved' | 'resolved';
  category?: 'tax' | 'audit';
}

/**
 * Hook to fetch audit events for tax event resolution workflow
 */
export const useAuditEvents = (params: UseAuditEventsParams) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<AuditEventsResponse>({
    queryKey: ["audit", "events", params.taxyear, params.errorType, params.status],
    queryFn: async () => {
      const token = await getAccessTokenSilently();

      const response = await authorizedAxios(token).get("/audit/events", {
        params: {
          taxyear: params.taxyear,
          errorType: params.errorType,
          status: params.status,
          category: params.category
        }
      });

      return response.data;
    },
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes (shorter than summary)
    retry: 2,
    enabled: true, // Always enabled - tax year is now optional
  });
};

/**
 * Hook to resolve an audit event
 */
export const useResolveAuditEvent = () => {
  const { getAccessTokenSilently } = useAuth0();

  return async (
    eventId: string,
    resolutionType: 'confirmed-zero' | 'excluded' | 'data-fixed' | 'loss-writeoff' | 'self-transfer-confirmed',
    notes?: string,
    candidateTxnIds?: number[]
  ) => {
    const token = await getAccessTokenSilently();

    const response = await authorizedAxios(token).post(
      `/audit/events/${eventId}/resolve`,
      {
        resolutionType,
        notes,
        candidateTxnIds,
      }
    );

    return response.data;
  };
};
